// ── GarageIQ Brand Icons (React Native) ─────────────────────────────────────
// Ported from garageiq-web/components/Icons.jsx. The web uses raw <svg>; here
// we use react-native-svg primitives. cssInterop registers <Svg> with
// NativeWind so callers can pass className like the web version.

import Svg, { Circle, Line, Path, Polygon, Polyline, Rect } from 'react-native-svg';
import { cssInterop } from 'nativewind';

// Make NativeWind's className work on the outer Svg element.
cssInterop(Svg, { className: 'style' });

// ── Brand Logo ────────────────────────────────────────────────────────────
export function GarageIQLogo({ size = 32, className = '' }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 48 48" fill="none" className={className}>
      <Path d="M24 6L6 22H42L24 6Z" fill="#F5F0E8" stroke="#F5F0E8" strokeWidth={1.5} strokeLinejoin="round" />
      <Rect x={10} y={22} width={28} height={20} rx={2} fill="#F5F0E8" />
      <Rect x={14} y={25} width={20} height={14} rx={1.5} fill="#2E3340" />
      <Line x1={14} y1={31} x2={34} y2={31} stroke="#1B2A4A" strokeWidth={1.2} />
      <Line x1={14} y1={36} x2={34} y2={36} stroke="#1B2A4A" strokeWidth={1.2} />
      <Circle cx={24} cy={33.5} r={1.5} fill="#4A8FD4" />
    </Svg>
  );
}

export function GarageIQLogoMark({ size = 24, className = '' }) {
  return <GarageIQLogo size={size} className={className} />;
}

// ── Section & UI Icons ────────────────────────────────────────────────────
// Convention: cream (#F5F0E8) outline, sky blue (#4A8FD4) accent.

const STROKE = '#F5F0E8';
const ACCENT = '#4A8FD4';

const baseProps = (size, className) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: STROKE,
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  className,
});

export function IconWrench({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </Svg>
  );
}

export function IconCar({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Path d="M5 17h14" />
      <Path d="M6 11l1.5-4.5A1 1 0 0 1 8.44 6h7.12a1 1 0 0 1 .95.68L18 11" />
      <Rect x={3} y={11} width={18} height={6} rx={2} />
      <Circle cx={7} cy={17} r={2} fill={ACCENT} stroke={ACCENT} />
      <Circle cx={17} cy={17} r={2} fill={ACCENT} stroke={ACCENT} />
    </Svg>
  );
}

export function IconGarage({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Path d="M3 21V10l9-7 9 7v11" />
      <Rect x={7} y={13} width={10} height={8} rx={1} stroke={STROKE} fill="none" />
      <Line x1={7} y1={16} x2={17} y2={16} stroke={STROKE} strokeWidth={1} />
      <Line x1={7} y1={19} x2={17} y2={19} stroke={STROKE} strokeWidth={1} />
      <Circle cx={12} cy={17.5} r={0.8} fill={ACCENT} />
    </Svg>
  );
}

export function IconDiagnose({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Circle cx={11} cy={11} r={7} />
      <Path d="M21 21l-4.35-4.35" />
      <Circle cx={11} cy={11} r={2} fill={ACCENT} stroke="none" />
    </Svg>
  );
}

export function IconShop({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <Polyline points="9 22 9 12 15 12 15 22" />
      <Circle cx={12} cy={15} r={1} fill={ACCENT} />
    </Svg>
  );
}

export function IconCart({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <Line x1={3} y1={6} x2={21} y2={6} />
      <Path d="M16 10a4 4 0 0 1-8 0" />
      <Circle cx={12} cy={14} r={1} fill={ACCENT} />
    </Svg>
  );
}

export function IconTools({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Path d="M12 2L2 7l10 5 10-5-10-5z" />
      <Path d="M2 17l10 5 10-5" />
      <Path d="M2 12l10 5 10-5" />
      <Circle cx={12} cy={12} r={1.2} fill={ACCENT} stroke="none" />
    </Svg>
  );
}

export function IconVideo({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Rect x={2} y={4} width={15} height={16} rx={2} />
      <Polygon points="22 8 17 12 22 16 22 8" fill={ACCENT} stroke={ACCENT} />
    </Svg>
  );
}

export function IconCheck({ size = 20, className = '' }) {
  // Note: IconCheck uses ACCENT as its stroke (different from base).
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <Polyline points="20 6 9 17 4 12" />
    </Svg>
  );
}

export function IconStar({ size = 14, className = '' }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill={ACCENT} stroke="none" className={className}>
      <Polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </Svg>
  );
}

export function IconSteps({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Line x1={8} y1={6} x2={21} y2={6} />
      <Line x1={8} y1={12} x2={21} y2={12} />
      <Line x1={8} y1={18} x2={21} y2={18} />
      <Circle cx={4} cy={6} r={1.2} fill={ACCENT} stroke="none" />
      <Circle cx={4} cy={12} r={1.2} fill={ACCENT} stroke="none" />
      <Circle cx={4} cy={18} r={1.2} fill={ACCENT} stroke="none" />
    </Svg>
  );
}

export function IconSafety({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <Circle cx={12} cy={11} r={2} fill={ACCENT} stroke="none" />
    </Svg>
  );
}

export function IconParts({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Circle cx={12} cy={12} r={3} />
      <Path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      <Circle cx={12} cy={12} r={1} fill={ACCENT} stroke="none" />
    </Svg>
  );
}

export function IconTrophy({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <Path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <Path d="M4 22h16" />
      <Path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22" />
      <Path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22" />
      <Path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
      <Circle cx={12} cy={7} r={1.5} fill={ACCENT} stroke="none" />
    </Svg>
  );
}

export function IconBook({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <Path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <Circle cx={12} cy={10} r={1} fill={ACCENT} stroke="none" />
    </Svg>
  );
}

export function IconDiagram({ size = 20, className = '' }) {
  return (
    <Svg {...baseProps(size, className)}>
      <Rect x={3} y={3} width={18} height={18} rx={2} />
      <Path d="M3 9h18" />
      <Path d="M9 21V9" />
      <Circle cx={15} cy={15} r={1.5} fill={ACCENT} stroke="none" />
    </Svg>
  );
}
